import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from './Layout';
import { Message, User } from '../types';
import { getImageUrl } from '../services/api';
import { SafeImage } from './SafeImage';

interface ChatScreenProps {
    friend: User;
    onBack: () => void;
    messages: Message[];
    currentUserId: string;
    onSendMessage: (msg: string) => void;
}

export const ChatScreen: React.FC<ChatScreenProps> = ({ friend, onBack, messages, currentUserId, onSendMessage }) => {
    const [msg, setMsg] = useState('');
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    return (
        <div style={{ height: '100%', background: 'var(--bg)', zIndex: 1000, display: 'flex', flexDirection: 'column', animation: 'slideL .3s cubic-bezier(.16,1,.3,1)' }}>
            {/* Header */}
            <div style={{ padding: '50px 18px 12px', background: 'rgba(10,10,15,.8)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)', zIndex: 10, flexShrink: 0 }}>
                <Row style={{ justifyContent: 'space-between' }}>
                    <Row style={{ gap: 12 }}>
                        <button onClick={onBack} className="icon-btn">←</button>
                        <Row style={{ gap: 10 }}>
                            <div style={{ position: 'relative' }}>
                                <div style={{ width: 42, height: 42, borderRadius: 14, overflow: 'hidden' }}>
                                    <SafeImage src={friend.profileImage ? getImageUrl(friend.profileImage) : undefined} fallbackSrc={`https://ui-avatars.com/api/?name=${friend.username}&background=random`} alt={friend.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                                {friend.isLive && <div style={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, background: 'var(--green)', borderRadius: '50%', border: '2px solid var(--bg)' }} />}
                            </div>
                            <Col>
                                <div className="syne" style={{ fontSize: 16, fontWeight: 700 }}>{friend.username}</div>
                                <div style={{ fontSize: 11, color: friend.isLive ? 'var(--green)' : 'var(--muted)' }}>
                                    {friend.isLive ? 'Çevrimiçi' : 'Son görülme: Yakınlarda'}
                                </div>
                            </Col>
                        </Row>
                    </Row>
                    <Row style={{ gap: 8 }}>
                        <button className="icon-btn" style={{ borderRadius: '50%', color: 'var(--cyan)' }}>📞</button>
                        <button className="icon-btn" style={{ borderRadius: '50%', color: 'var(--purple)' }}>📹</button>
                    </Row>
                </Row>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 18px' }} className="hide-scrollbar">
                <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', marginBottom: 24 }}>Bugün</div>
                <Col style={{ gap: 16 }}>
                    {messages.map((m, i) => {
                        const senderId = m.sender.id || (m.sender as any)._id;
                        const isMe = senderId === currentUserId;
                        return (
                            <Row key={m._id || i} style={{ justifyContent: isMe ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 8 }}>
                                {!isMe && (
                                    <div style={{ width: 28, height: 28, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
                                        <SafeImage src={friend.profileImage ? getImageUrl(friend.profileImage) : undefined} fallbackSrc={`https://ui-avatars.com/api/?name=${friend.username}&background=random`} alt={friend.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </div>
                                )}
                                <div style={{
                                    maxWidth: '75%', padding: '12px 16px', fontSize: 14, lineHeight: 1.4,
                                    background: isMe ? 'linear-gradient(135deg,var(--coral),var(--orange))' : 'var(--surface2)',
                                    color: isMe ? '#fff' : 'var(--text)',
                                    borderRadius: isMe ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                    boxShadow: isMe ? '0 8px 24px rgba(255,92,92,.25)' : 'none'
                                }}>
                                    {m.content}
                                </div>
                            </Row>
                        );
                    })}
                    <div ref={endRef} />
                </Col>
            </div>

            {/* Input */}
            <div style={{ padding: '12px 18px 34px', background: 'var(--bg)', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                <Row style={{ gap: 10 }}>
                    <button className="icon-btn" style={{ border: 'none', background: 'var(--surface2)', borderRadius: 14 }}>➕</button>
                    <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 18, display: 'flex', alignItems: 'center', padding: '4px 4px 4px 16px' }}>
                        <input
                            value={msg} onChange={e => setMsg(e.target.value)}
                            onKeyPress={e => { if (e.key === 'Enter') { onSendMessage(msg); setMsg(''); } }}
                            placeholder="Mesaj yaz..."
                            style={{ flex: 1, background: 'none', border: 'none', color: '#fff', fontSize: 14, outline: 'none' }}
                        />
                        <button
                            onClick={() => { if (msg.trim()) { onSendMessage(msg); setMsg(''); } }}
                            style={{ width: 38, height: 38, borderRadius: 14, background: msg.trim() ? 'var(--coral)' : 'var(--surface2)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s' }}>
                            ➤
                        </button>
                    </div>
                </Row>
            </div>
        </div>
    );
};
